import { isolateComponent, labelStyles, withModifier, } from '@zapier/style-encapsulation';
import { normalizePathname } from '@zapier/url-utils';
import { Colors } from '@zapier/design-system';
import UniversalNavLink from '../UniversalNavLink';
import UniversalSidebarSubmenu from '../UniversalSidebar/UniversalSidebarSubmenu/UniversalSidebarSubmenu';
import { UNIVERSAL_SIDEBAR_COLLAPSED_WIDTH, UNIVERSAL_SIDEBAR_WIDTH, skipLinkHref, } from '../../constants';
import { useSidebarLinkData } from '../../hooks';
const GAP = 20;
const Styles = labelStyles('UniversalSidebar', {
    root: (props) => [
        {
            position: 'relative',
            display: 'flex',
            flexDirection: 'column',
            backgroundColor: Colors.neutral100,
            padding: '30px 10px 0 10px',
            willChange: 'transform, width',
        },
        !props.isFullWidth &&
            withModifier('constrainedWidth', {
                width: UNIVERSAL_SIDEBAR_WIDTH,
                maxWidth: '100vw',
            }),
        !props.isFullWidth &&
            withModifier('bordered', {
                '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    right: 0,
                    bottom: 0,
                    width: 1,
                    backgroundColor: Colors.gray20,
                },
            }),
        props.isShadowed &&
            withModifier('shadowed', {
                '&::after': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: '100%',
                    bottom: 0,
                    width: 10,
                    pointerEvents: 'none',
                    background: `linear-gradient(
            90deg,
            rgba(0, 0, 0, 0.10) 0%,
            rgba(0, 0, 0, 0.03) 50%,
            rgba(0, 0, 0, 0.00) 100%
          )`,
                },
            }),
        props.isCollapsed &&
            withModifier('collapsed', {
                width: UNIVERSAL_SIDEBAR_COLLAPSED_WIDTH,
                overflow: 'visible',
            }),
    ],
    header: {
        flex: 'none',
    },
    content: (props) => [
        {
            overflow: 'auto',
            paddingTop: GAP,
            paddingBottom: GAP,
            flexBasis: 'auto',
            flexShrink: 1,
            flexGrow: 0,
            scrollbarWidth: 'none',
            '::-webkit-scrollbar': {
                display: 'none',
            },
        },
        props.isCollapsed &&
            withModifier('collapsed', {
                overflow: 'visible',
            }),
    ],
    links: (props) => [
        Object.assign({ alignContent: 'start', display: 'grid', gap: 10 }, (!props.isCollapsed && { padding: '0 2px' })),
    ],
    footer: {
        flex: 'none',
        borderTop: `1px solid ${Colors.neutral300}`,
        paddingTop: GAP,
        paddingBottom: GAP,
    },
});
export const isLinkSelected = (linkHref, currentPageHref) => {
    if (currentPageHref.startsWith('/')) {
        return normalizePathname(linkHref).endsWith(normalizePathname(currentPageHref));
    }
    let parsedSelectedHref;
    try {
        parsedSelectedHref = new URL(currentPageHref);
    }
    catch (_a) {
        return normalizePathname(linkHref).endsWith(normalizePathname(currentPageHref));
    }
    const hostname = parsedSelectedHref.hostname;
    if (hostname === 'zapier.com' || hostname === 'zapier-staging.com') {
        return normalizePathname(linkHref).endsWith(normalizePathname(parsedSelectedHref.pathname));
    }
    if (hostname.split('.').length > 2) {
        return linkHref.includes(hostname);
    }
    return normalizePathname(linkHref).endsWith(normalizePathname(currentPageHref));
};
const PropLink = ({ isCollapsed, isSelected, link, }) => {
    let icon = link.icon;
    if (typeof icon === 'object') {
        if (isSelected) {
            icon = icon.selected;
        }
        else {
            icon = icon.unselected;
        }
    }
    const linkOrMenu = link.submenu && !link.href ? (isolateComponent(UniversalSidebarSubmenu, { links: link.submenu, text: link.text })) : (isolateComponent(UniversalNavLink, { href: link.href, icon: icon, isCollapsed: isCollapsed, isSelected: isSelected, isSidebar: true, text: link.text }));
    return isolateComponent("li", null, linkOrMenu);
};
const Links = (props) => {
    var _a;
    const links = (_a = props.links) === null || _a === void 0 ? void 0 : _a.map((link) => {
        const isSelected = isLinkSelected(link.href || '', props.selectedHref);
        return (isolateComponent(PropLink, { isCollapsed: props.isCollapsed, isSelected: isSelected, key: link.text, link: link }));
    });
    return isolateComponent("ul", { css: Styles.links(props) }, links);
};
const UniversalSidebar = (props) => {
    const { sideNavData } = useSidebarLinkData();
    const mouseProps = {
        onClick: props.onClick,
        onMouseEnter: props.onMouseEnter,
        onMouseLeave: props.onMouseLeave,
    };
    const subheaderContent = props.renderSubheader && props.renderSubheader(props);
    const subheader = subheaderContent && (isolateComponent("div", { css: Styles.header }, subheaderContent));
    const footerContent = props.renderFooter();
    const footer = footerContent && (isolateComponent("div", { css: Styles.footer, role: "region" }, footerContent));
    const linksToRender = props.links || sideNavData;
    return (isolateComponent("nav", Object.assign({ "aria-label": "Site Links", css: Styles.root(props), "data-testid": "universalSidebar" }, mouseProps),
        subheader,
        isolateComponent("div", { css: Styles.content(props) },
            isolateComponent(Links, Object.assign({}, props, { links: linksToRender }))),
        footer));
};
UniversalSidebar.defaultProps = {
    isCollapsed: false,
    isFullWidth: true,
    logoHref: '/',
    logoAriaLabel: 'Go to homepage',
    renderFooter: () => null,
    selectedHref: '',
    skipLinkHref,
};
export default UniversalSidebar;
//# sourceMappingURL=UniversalSidebar.js.map
// @zapier/universal-layout@8.3.7 (9c955f548)
